import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronRight, Zap, CheckCircle2, ChevronLeft, RotateCcw } from 'lucide-react';
import VideoModal from './VideoModal';

const videoTypes = [
  { value: 'explainer-demo', title: 'Explainer Video', youtubeId: 'UCledcrzPks', thumbnail: 'https://img.youtube.com/vi/UCledcrzPks/hqdefault.jpg' },
  { value: 'product-launch', title: 'Product Launch', youtubeId: 'wnzbApqFAsE', thumbnail: 'https://img.youtube.com/vi/wnzbApqFAsE/hqdefault.jpg' },
  { value: 'tutorial-training', title: 'Tutorials', youtubeId: '7qMrpl72_rU', thumbnail: 'https://img.youtube.com/vi/7qMrpl72_rU/hqdefault.jpg' },
  { value: 'sales-pitch', title: 'Sales Pitch', youtubeId: 'T7aIxowa2Yk', thumbnail: 'https://img.youtube.com/vi/T7aIxowa2Yk/hqdefault.jpg' },
  { value: 'event-booth', title: 'Event Video', youtubeId: 'TEENRIHGcAQ', thumbnail: 'https://img.youtube.com/vi/TEENRIHGcAQ/hqdefault.jpg' },
  { value: 'pitch-deck', title: 'Pitch Deck', youtubeId: 'FPhg_ZjrPtU', thumbnail: 'https://img.youtube.com/vi/FPhg_ZjrPtU/hqdefault.jpg' },
  { value: 'case-study', title: 'Case Study', youtubeId: 'ARvdwcmhjps', thumbnail: 'https://img.youtube.com/vi/ARvdwcmhjps/hqdefault.jpg' },
  { value: 'testimonial', title: 'Testimonials', youtubeId: '9_z_h9BqhVg', thumbnail: 'https://img.youtube.com/vi/9_z_h9BqhVg/hqdefault.jpg' },
  { value: 'social-ads', title: 'Social Ads', youtubeId: 'bwa5rUWkTko', thumbnail: 'https://img.youtube.com/vi/bwa5rUWkTko/hqdefault.jpg' },
  { value: 'brand-tv', title: 'TV Ads', youtubeId: 'IBA1my0beR4', thumbnail: 'https://img.youtube.com/vi/IBA1my0beR4/hqdefault.jpg' },
  { value: 'gif-ui', title: 'UI Animation', youtubeId: 'UCledcrzPks', thumbnail: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=225&fit=crop' },
  { value: 'ugc-influencer', title: 'UGC', youtubeId: 'bwa5rUWkTko', thumbnail: 'https://img.youtube.com/vi/bwa5rUWkTko/hqdefault.jpg' },
  { value: 'corporate', title: 'Corporate', youtubeId: 'kvGZBGmWO0A', thumbnail: 'https://img.youtube.com/vi/kvGZBGmWO0A/hqdefault.jpg' },
  { value: 'youtube-seo', title: 'YouTube SEO', youtubeId: 'XP6dqsEQyJE', thumbnail: 'https://img.youtube.com/vi/XP6dqsEQyJE/hqdefault.jpg' },
  { value: 'stock-footage', title: 'Stock Footage', youtubeId: 'SB26MowGMDM', thumbnail: 'https://img.youtube.com/vi/SB26MowGMDM/hqdefault.jpg' },
  { value: 'app-video', title: 'App Video', youtubeId: 'FFewDlGHVKo', thumbnail: 'https://img.youtube.com/vi/FFewDlGHVKo/hqdefault.jpg' }
];

const goalToVideoTypesMapping = {
  'convert-visitors': ['explainer-demo', 'sales-pitch', 'case-study'],
  'lead-generation': ['sales-pitch', 'case-study', 'ugc-influencer', 'social-ads'],
  'simplify-product': ['explainer-demo', 'tutorial-training'],
  'launch-product': ['product-launch', 'brand-tv', 'social-ads', 'ugc-influencer'],
  'raise-funding': ['pitch-deck', 'corporate', 'stock-footage'],
  'build-trust': ['case-study', 'testimonial', 'corporate'],
  'event-success': ['event-booth', 'product-launch'],
  'success-stories': ['case-study', 'testimonial', 'stock-footage', 'corporate'],
  'market-expansion': ['sales-pitch', 'stock-footage', 'case-study'],
  'rank-google': ['youtube-seo', 'tutorial-training', 'ugc-influencer'],
  'speed-onboarding': ['tutorial-training', 'gif-ui', 'explainer-demo'],
  'improve-ctr': ['social-ads', 'gif-ui'],
  'social-presence': ['social-ads', 'ugc-influencer', 'brand-tv'],
  'brand-awareness': ['brand-tv', 'corporate', 'social-ads'],
  'company-culture': ['corporate', 'stock-footage'],
  'train-users': ['tutorial-training'],
  'reduce-support': ['tutorial-training', 'youtube-seo'],
  'product-updates': ['product-launch', 'gif-ui', 'tutorial-training'],
  'app-downloads': ['app-video', 'social-ads', 'explainer-demo'],
  'shareholder-report': ['corporate', 'pitch-deck', 'stock-footage'],
  'stand-out': ['brand-tv', 'explainer-demo', 'case-study']
};

const StepTwo = ({ selectedVideoTypes, selectedGoal, onVideoTypesChange, onNext, onPrev }) => {
  const [isReady, setIsReady] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);

  useEffect(() => {
    if (selectedGoal && goalToVideoTypesMapping[selectedGoal]) {
      onVideoTypesChange(goalToVideoTypesMapping[selectedGoal]);
    }
    setTimeout(() => setIsReady(true), 300);
  }, [selectedGoal]);

  const toggleSelection = (val) => {
    const newSelection = selectedVideoTypes.includes(val)
      ? selectedVideoTypes.filter(t => t !== val)
      : [...selectedVideoTypes, val];
    onVideoTypesChange(newSelection);
  };

  const handleReset = (e) => {
    e.stopPropagation();
    onVideoTypesChange([]);
  };

  return (
    <div className="w-full mt-16 space-y-12">
      {/* Header with Integrated Controls */}
      <div className="flex flex-col xl:flex-row items-center justify-between gap-8 pb-10 border-b border-white/10">
        <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
          Choose Your <span className="text-amber-500">Video</span>
        </h2>
        
        <div className="flex flex-wrap items-center justify-center gap-4">
          {/* Back Button */}
          <button 
            onClick={onPrev}
            className="px-6 py-3 rounded-xl border border-white/10 text-white font-bold uppercase tracking-widest hover:bg-white/5 transition-all flex items-center gap-2 text-sm"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          {/* Selection Status Group */}
          <div className="flex items-center gap-3">
            {/* Count Badge */}
            <div className="px-5 py-3 bg-amber-500/10 rounded-xl border border-amber-500/20 flex items-center shadow-[0_0_20px_rgba(245,158,11,0.05)]">
              <span className="text-gray-400 text-[10px] font-mono uppercase tracking-[0.2em] hidden sm:inline">Selected: </span>
              <span className="text-amber-500 font-black text-xl sm:ml-3">{selectedVideoTypes.length}</span>
            </div>
            
            {/* Reset Icon - Just Outside as Requested */}
            <AnimatePresence>
              {selectedVideoTypes.length > 0 && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8, x: -10 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.8, x: -10 }}
                  onClick={handleReset}
                  className="p-3 rounded-xl bg-white/5 border border-white/10 text-gray-500 hover:text-amber-500 hover:border-amber-500/50 transition-all group"
                  title="Reset Selection"
                >
                  <RotateCcw className="w-5 h-5 group-hover:rotate-[-45deg] transition-transform" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* Next Button */}
          <motion.button
            whileHover={selectedVideoTypes.length > 0 ? { scale: 1.02 } : {}}
            whileTap={selectedVideoTypes.length > 0 ? { scale: 0.98 } : {}}
            onClick={onNext}
            disabled={selectedVideoTypes.length === 0}
            className={`px-10 py-3 rounded-xl font-black text-base tracking-[0.15em] flex items-center justify-center gap-3 transition-all duration-300 ${
              selectedVideoTypes.length > 0
                ? 'bg-amber-500 text-black shadow-[0_0_25px_rgba(245,158,11,0.2)] hover:bg-amber-400'
                : 'bg-white/5 text-gray-600 cursor-not-allowed border border-white/10'
            }`}
          >
            NEXT <ChevronRight className="w-5 h-5 stroke-[3]" />
          </motion.button>
        </div>
      </div>

      {/* Grid Selection */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-20">
        {videoTypes.map((video, idx) => {
          const isSelected = selectedVideoTypes.includes(video.value);
          const isRecommended = goalToVideoTypesMapping[selectedGoal]?.includes(video.value);
          
          return (
            <motion.div
              key={video.value}
              initial={{ opacity: 0, y: 20 }}
              animate={isReady ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: idx * 0.05 }}
              className={`relative group rounded-2xl overflow-hidden border-2 transition-all duration-500 ${
                isSelected 
                  ? 'border-amber-500 shadow-[0_0_40px_rgba(245,158,11,0.1)]' 
                  : 'border-white/10 hover:border-white/30'
              }`}
            >
              <div className="relative aspect-video overflow-hidden cursor-pointer" onClick={() => toggleSelection(video.value)}>
                <img 
                  src={video.thumbnail} 
                  alt={video.title} 
                  className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${isSelected ? 'brightness-75' : 'brightness-50'}`} 
                />
                <div className="absolute inset-0 bg-black/50 transition-opacity duration-300 group-hover:opacity-30" />
                
                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.button
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewVideo(video);
                    }}
                    className="p-4 bg-amber-500 text-black rounded-full shadow-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0"
                  >
                    <Play className="w-5 h-5 fill-current" />
                  </motion.button>
                </div>

                <div className={`absolute top-4 right-4 transition-all duration-500 ${isSelected ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
                  <div className="bg-amber-500 p-1.5 rounded-full shadow-lg">
                    <CheckCircle2 className="w-4 h-4 text-black" />
                  </div>
                </div>
                {isRecommended && (
                  <div className="absolute top-4 left-4">
                    <div className="bg-amber-500/20 backdrop-blur-md border border-amber-500/50 text-amber-500 text-[9px] font-black uppercase px-2 py-1 rounded-md flex items-center gap-1">
                      <Zap className="w-3 h-3 fill-current" /> Recommended
                    </div>
                  </div>
                )}
              </div>
              <div className={`p-5 transition-colors duration-300 ${isSelected ? 'bg-amber-500/10' : 'bg-white/5'}`}>
                <h3 className={`font-bold tracking-tight text-lg transition-colors ${isSelected ? 'text-amber-500' : 'text-white'}`}>
                  {video.title}
                </h3>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {previewVideo && (
          <VideoModal 
            video={previewVideo} 
            onClose={() => setPreviewVideo(null)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default StepTwo;